import type { Profile } from '@/constant/schema';
import { getCanonicalProfileRole, type CanonicalProfileRole } from '@/lib/profile-role';

type RoutingProfile = Pick<Profile, 'onboard_data' | 'is_onboarding_finished' | 'role'> | null | undefined;
type PersistedRole = CanonicalProfileRole;

export const ONBOARD_INFO_PATH = '/onboard/info';
export const ONBOARD_PREPARE_PATH = '/onboard/prepare';
export const QUEST_PATH = '/quest';
export const PARENT_PATH = '/parent';
export const TEACHER_PATH = '/teacher';
export const PARENT_CREATE_STUDENT_PATH = '/onboard/parent-student';

const PARENT_ONBOARDING_PATH = PARENT_CREATE_STUDENT_PATH;

export function getNormalizedOnboardRole(profile: RoutingProfile): string {
  return getCanonicalProfileRole(profile);
}

function buildOnboardingInfoPath(redirectTarget: string): string {
  return `${ONBOARD_INFO_PATH}?redirect=${encodeURIComponent(redirectTarget)}`;
}

export function isParentV2Role(role: string): boolean {
  return role === 'parent_v2';
}

export function isParentV2Profile(profile: RoutingProfile): boolean {
  return isParentV2Role(getNormalizedOnboardRole(profile));
}

export function isTeacherRole(role: string): boolean {
  return role === 'teacher';
}

export function isTeacherProfile(profile: RoutingProfile): boolean {
  return isTeacherRole(getNormalizedOnboardRole(profile));
}

export function resolvePersistedOnboardRole(profile: RoutingProfile): PersistedRole {
  return getCanonicalProfileRole(profile);
}

export function getOnboardingEntryPath(profile: RoutingProfile, redirectTarget?: string | null): string {
  if (isParentV2Profile(profile)) {
    return PARENT_ONBOARDING_PATH;
  }

  if (isTeacherProfile(profile)) {
    return TEACHER_PATH;
  }

  if (redirectTarget) {
    return buildOnboardingInfoPath(redirectTarget);
  }

  return ONBOARD_INFO_PATH;
}

export function getPostOnboardingInfoPath(profile: RoutingProfile): string {
  return isParentV2Profile(profile) ? PARENT_PATH : ONBOARD_PREPARE_PATH;
}

export function getPostPreparePath(redirectTo: string | null): string {
  return redirectTo || QUEST_PATH;
}

export function getPostLoginPath(profile: RoutingProfile, redirectTarget: string | null): string {
  if (redirectTarget) {
    return redirectTarget;
  }

  if (isParentV2Profile(profile)) {
    return PARENT_PATH;
  }

  if (isTeacherProfile(profile)) {
    return TEACHER_PATH;
  }

  return QUEST_PATH;
}
